import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, Alert, Chip, Tabs, Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DetailRow } from '../../components/InvField';
import WoPortalLinkCard from '../../components/material/WoPortalLinkCard';
import WoQuoteComparison from '../../components/material/WoQuoteComparison';
import WoBreakupList from '../../components/material/WoBreakupList';
import {
  getWorkOrder, getWorkOrderQuotes, publishWorkOrder, issueWorkOrder, cancelWorkOrder,
  startWorkOrder, holdWorkOrder, resumeWorkOrder, completeWorkOrder,
  closeWorkOrder, awardWorkOrder,
} from '../../api/workOrder';
import { getPurchasableVendors } from '../../api/vendor';
import {
  fromApiWorkOrder, fromApiWoQuote, woIsBid, woHasBidHistory, woLineAmounts, asWholeNumber,
  isHumanWoReference, formatWoActivityMeta,
} from '../../utils/woEntity';
import { notifyWoInvitedVendors, summarizeWoInviteNotify } from '../../utils/woInviteNotify';
import { formatCurrency, formatDate, formatDateTime } from '../../utils';
import { hasDisplayValue } from '../../utils/poLifecycle';
import { WO_BID_WORKFLOW_STAGES, WO_WORKFLOW_STAGES } from '../../constants/workOrder';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS, canWorkOrder } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';
import useItemNames from '../../hooks/useItemNames';
import { isUuidLike } from '../../utils/itemDisplay';

const TAB_OVERVIEW = 'overview';
const TAB_VENDORS = 'vendors';
const TAB_QUOTES = 'quotes';
const TAB_COMPARISON = 'comparison';

function collectWoVendorIds(wo, quotes) {
  const ids = [];
  const add = (id) => {
    const value = String(id || '').trim();
    if (value && !ids.includes(value)) ids.push(value);
  };
  (wo?.invited_vendor_ids || []).forEach(add);
  (wo?.invited_vendors || []).forEach((row) => add(row?.vendor_id || row?.id));
  (quotes || []).forEach((q) => add(q.vendor_id));
  return ids;
}

function WoSummaryStrip({ invitedCount, respondedCount, lowestNet }) {
  const awaiting = Math.max(invitedCount - respondedCount, 0);
  const stats = [
    { label: 'Invited', value: invitedCount },
    { label: 'Responded', value: respondedCount },
    { label: 'Awaiting', value: awaiting },
    {
      label: 'Lowest quote',
      value: lowestNet == null ? '—' : formatCurrency(lowestNet),
      wide: true,
    },
  ];
  return (
    <Box sx={{
      display: 'flex', flexWrap: 'wrap', gap: 0, mb: 2.5,
      border: `1px solid ${IPD_COLORS.border}`, borderRadius: '12px',
      backgroundColor: IPD_COLORS.cardBg, overflow: 'hidden',
    }}>
      {stats.map((stat, i) => (
        <Box
          key={stat.label}
          sx={{
            flex: stat.wide ? '2 1 200px' : '1 1 120px',
            px: 2, py: 1.5,
            borderLeft: i === 0 ? 'none' : `1px solid ${IPD_COLORS.border}`,
          }}
        >
          <Typography sx={{
            fontSize: 11, fontWeight: 600, letterSpacing: '.06em',
            textTransform: 'uppercase', color: IPD_COLORS.textSecondary,
          }}>
            {stat.label}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: IPD_COLORS.textPrimary, lineHeight: 1.2 }}>
            {stat.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function WoWorkflowBar({ status, bidMode }) {
  const stages = bidMode ? WO_BID_WORKFLOW_STAGES : WO_WORKFLOW_STAGES;
  const currentStatus = status === 'AWARDED' ? 'ISSUED' : status;
  const currentIndex = Math.max(0, stages.findIndex((stage) => stage.key === currentStatus));
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      mb: 2.5, p: { xs: 1.5, sm: 2 }, borderRadius: '12px',
      border: `1px solid ${IPD_COLORS.border}`, backgroundColor: IPD_COLORS.cardBg,
    }}>
      {stages.map((stage, i) => {
        const isCompleted = currentIndex > i;
        const isActive = currentIndex === i;
        return (
          <React.Fragment key={stage.key}>
            <Chip
              label={stage.label}
              size="small"
              sx={{
                fontWeight: 700,
                backgroundColor: isCompleted || isActive ? IPD_COLORS.primary : IPD_COLORS.selectedBg,
                color: isCompleted || isActive ? '#fff' : IPD_COLORS.textSecondary,
              }}
            />
            {i < stages.length - 1 && (
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12, display: { xs: 'none', sm: 'inline' } }}>
                →
              </Typography>
            )}
          </React.Fragment>
        );
      })}
      <Box sx={{ flex: { xs: '1 1 100%', sm: 1 }, display: { xs: 'none', sm: 'block' } }} />
      <StatusChip status={status} />
      {bidMode && <StatusChip status="PUBLISHED" label="Bid" />}
    </Box>
  );
}

export default function WoDetail() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { getPurchaseUomCode } = useItemNames();
  const listUrl = url.replace(/\/work-orders\/[^/]+$/, '/work-orders');
  const editUrl = `${url}/edit`;

  const [row, setRow] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [acting, setActing] = useState(false);
  const [reason, setReason] = useState('');
  const [activeTab, setActiveTab] = useState(TAB_OVERVIEW);
  const [emailingVendors, setEmailingVendors] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const wo = fromApiWorkOrder(await getWorkOrder(id));
      setRow(wo);
      const [quotePayload, vendorList] = await Promise.all([
        woHasBidHistory(wo) ? getWorkOrderQuotes(id).catch(() => wo.quotes || []) : Promise.resolve(wo.quotes || []),
        getPurchasableVendors().catch(() => []),
      ]);
      const list = Array.isArray(quotePayload)
        ? quotePayload
        : (quotePayload?.quotes || quotePayload?.items || quotePayload?.data || wo.quotes || []);
      setQuotes(list.map(fromApiWoQuote));
      setVendors(Array.isArray(vendorList) ? vendorList : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setActiveTab(TAB_OVERVIEW);
  }, [id]);

  const bid = woIsBid(row);
  const bidHistory = woHasBidHistory(row);
  const status = row?.status;
  const items = row?.items || [];
  const invitedIds = row?.invited_vendor_ids || [];
  const vendorIds = useMemo(() => collectWoVendorIds(row, quotes), [row, quotes]);
  const invitedVendors = useMemo(() => vendorIds.map((vid) => {
    const match = vendors.find((v) => String(v.vendor_id || v.id) === String(vid));
    const invited = invitedIds.map(String).includes(String(vid));
    const quote = quotes.find((q) => String(q.vendor_id) === String(vid));
    return {
      id: vid,
      label: match?.vendor_name || quote?.vendor_name || vid,
      invited,
      quoted: Boolean(quote),
    };
  }), [vendorIds, vendors, invitedIds, quotes]);
  const lowestNet = useMemo(() => {
    const amounts = quotes.map((q) => Number(q.grand_total)).filter((n) => Number.isFinite(n) && n > 0);
    return amounts.length ? Math.min(...amounts) : null;
  }, [quotes]);

  const canEdit = canWorkOrder(can, INV_ACTIONS.WO_EDIT);
  const canPublish = canWorkOrder(can, INV_ACTIONS.WO_PUBLISH);
  const canAward = canWorkOrder(can, INV_ACTIONS.WO_AWARD);
  const canCancel = canWorkOrder(can, INV_ACTIONS.WO_CANCEL);
  const canExecute = canWorkOrder(can, INV_ACTIONS.WO_EXECUTE);
  const showQuotesTab = bidHistory && !['DRAFT'].includes(status);
  const showComparisonTab = showQuotesTab;

  useEffect(() => {
    const allowed = new Set([TAB_OVERVIEW]);
    if (bidHistory) allowed.add(TAB_VENDORS);
    if (showQuotesTab) allowed.add(TAB_QUOTES);
    if (showComparisonTab) allowed.add(TAB_COMPARISON);
    if (!allowed.has(activeTab)) setActiveTab(TAB_OVERVIEW);
  }, [activeTab, bidHistory, showQuotesTab, showComparisonTab]);

  async function runAction() {
    if (!dialog) return;
    setActing(true);
    try {
      if (dialog.action === 'publish') {
        await publishWorkOrder(id);
        const notify = await notifyWoInvitedVendors(id, {
          wo: row,
          vendorIds: invitedIds,
        }).catch((err) => ({ sent: [], failed: [{ reason: err.message }] }));
        toast.success(`Work bid published. ${summarizeWoInviteNotify(notify)}`);
      }
      if (dialog.action === 'issue') await issueWorkOrder(id);
      if (dialog.action === 'cancel') await cancelWorkOrder(id);
      if (dialog.action === 'start') await startWorkOrder(id);
      if (dialog.action === 'hold') await holdWorkOrder(id, { remarks: reason });
      if (dialog.action === 'resume') await resumeWorkOrder(id);
      if (dialog.action === 'complete') {
        await completeWorkOrder(id, {
          completed_by: reason,
          completion_remarks: reason,
          actual_completion: dayjs().toISOString(),
        });
      }
      if (dialog.action === 'close') await closeWorkOrder(id);
      if (dialog.action === 'award') {
        await awardWorkOrder(id, { quote_id: dialog.quoteId });
        toast.success('Quote awarded. This work order is now issued.');
      }
      setDialog(null);
      await load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  async function handleEmailVendors() {
    setEmailingVendors(true);
    try {
      const notify = await notifyWoInvitedVendors(id, {
        wo: row,
        vendorIds: vendorIds.length ? vendorIds : invitedIds,
      });
      const summary = summarizeWoInviteNotify(notify);
      const sentN = Array.isArray(notify.sent) ? notify.sent.length : (notify.sent || 0);
      const failedN = Array.isArray(notify.failed) ? notify.failed.length : (notify.failed || 0);
      if (sentN > 0 && !failedN) toast.success(summary);
      else toast.warn(summary);
    } catch (e) {
      toast.error(e.message || 'Could not email invited vendors');
    } finally {
      setEmailingVendors(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }
  if (error || !row) {
    return (
      <Box>
        <PageHeader title="Work Order" actions={<InvButton variant="outlined" onClick={() => history.push(listUrl)}>Back</InvButton>} />
        <Alert severity="error">{error || 'Not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Work Orders"
        title={row.wo_number}
        description={row.title}
        actions={(
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.push(listUrl)}>Back</InvButton>
            {status === 'DRAFT' && canEdit && (
              <InvButton size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => history.push(editUrl)}>Edit</InvButton>
            )}
            {status === 'DRAFT' && bid && canPublish && (
              <InvButton
                size="small"
                tone="mint"
                onClick={() => setDialog({
                  action: 'publish',
                  title: 'Publish bid',
                  desc: `Publish "${row.wo_number}" and email ${invitedIds.length} invited vendor(s)?`,
                })}
              >
                Publish
              </InvButton>
            )}
            {status === 'DRAFT' && !bid && canPublish && (
              <InvButton size="small" tone="mint" onClick={() => setDialog({ action: 'issue', title: 'Issue Work Order', desc: `Issue to ${row.vendor_name || 'the vendor'}?` })}>Issue to vendor</InvButton>
            )}
            {bid && status === 'PUBLISHED' && canPublish && (
              <InvButton
                size="small"
                variant="outlined"
                tone="teal"
                loading={emailingVendors}
                onClick={handleEmailVendors}
              >
                Email vendors
              </InvButton>
            )}
            {status === 'ISSUED' && canExecute && (
              <InvButton size="small" tone="mint" onClick={() => setDialog({ action: 'start', title: 'Start work', desc: 'Mark this Work Order as in progress?' })}>Start work</InvButton>
            )}
            {status === 'IN_PROGRESS' && canExecute && (
              <>
                <InvButton size="small" variant="outlined" tone="amber" onClick={() => setDialog({ action: 'hold', title: 'Hold work', desc: 'Pause this Work Order?' })}>Hold</InvButton>
                <InvButton size="small" tone="mint" onClick={() => setDialog({ action: 'complete', title: 'Mark completed', desc: 'Record completion details.' })}>Complete</InvButton>
              </>
            )}
            {status === 'ON_HOLD' && canExecute && (
              <InvButton size="small" tone="mint" onClick={() => setDialog({ action: 'resume', title: 'Resume work', desc: 'Move back to in progress?' })}>Resume</InvButton>
            )}
            {status === 'COMPLETED' && canExecute && (
              <InvButton size="small" tone="mint" onClick={() => setDialog({ action: 'close', title: 'Close Work Order', desc: 'Close this Work Order?' })}>Close</InvButton>
            )}
            {row.issued_wo_id && (
              <InvButton size="small" variant="outlined" onClick={() => history.push(url.replace(/\/work-orders\/[^/]+$/, `/work-orders/${row.issued_wo_id}`))}>
                Open work order{row.issued_wo_number ? ` ${row.issued_wo_number}` : ''}
              </InvButton>
            )}
            {!bid && row.source_bid_id && (
              <InvButton size="small" variant="outlined" onClick={() => history.push(url.replace(/\/work-orders\/[^/]+$/, `/work-orders/${row.source_bid_id}`))}>
                View bid{row.other_reference ? ` ${row.other_reference}` : ''}
              </InvButton>
            )}
            {['DRAFT', 'PUBLISHED', 'ISSUED', 'IN_PROGRESS', 'ON_HOLD'].includes(status) && canCancel && (
              <InvButton size="small" variant="outlined" tone="red" onClick={() => setDialog({ action: 'cancel', title: 'Cancel', desc: 'Cancel this record? The RFQ, if any, is left untouched.', destructive: true })}>Cancel</InvButton>
            )}
          </Box>
        )}
      />

      <WoWorkflowBar status={status} bidMode={bidHistory && !row.source_bid_id} />

      {bid && status === 'AWARDED' && row.issued_wo_id && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: '12px' }}>
          This is the bid record. The job is tracked as work order{' '}
          {row.issued_wo_number || 'issued after award'}.
        </Alert>
      )}
      {!bid && row.source_bid_id && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: '12px' }}>
          Awarded from bid {row.other_reference || 'the vendor quotation'}. Quote history stays on that bid.
        </Alert>
      )}

      {bid && (
        <WoSummaryStrip
          invitedCount={invitedIds.length}
          respondedCount={quotes.length}
          lowestNet={lowestNet}
        />
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2.5, borderBottom: `1px solid ${IPD_COLORS.border}` }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Overview" value={TAB_OVERVIEW} />
        {bidHistory && <Tab label={`Vendors (${invitedVendors.length})`} value={TAB_VENDORS} />}
        {showQuotesTab && <Tab label={`Quotes (${quotes.length})`} value={TAB_QUOTES} />}
        {showComparisonTab && <Tab label="Comparison" value={TAB_COMPARISON} />}
      </Tabs>

      {activeTab === TAB_OVERVIEW && (
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} md={5}>
            <SectionCard title="Work Order Details" sx={{ height: '100%' }}>
              <DetailRow label="Number">{row.wo_number}</DetailRow>
              {hasDisplayValue(row.title) && <DetailRow label="Title">{row.title}</DetailRow>}
              {hasDisplayValue(row.wo_type) && <DetailRow label="Type">{row.wo_type}</DetailRow>}
              {hasDisplayValue(row.priority) && <DetailRow label="Priority">{row.priority}</DetailRow>}
              {hasDisplayValue(row.wo_date) && <DetailRow label="Issue Date">{formatDate(row.wo_date)}</DetailRow>}
              {hasDisplayValue(row.submission_deadline) && (
                <DetailRow label="Submission Deadline">{formatDateTime(row.submission_deadline)}</DetailRow>
              )}
              {hasDisplayValue(row.department) && <DetailRow label="Department">{row.department}</DetailRow>}
              {hasDisplayValue(row.location) && <DetailRow label="Location">{row.location}</DetailRow>}
              {hasDisplayValue(row.asset) && <DetailRow label="Asset">{row.asset}</DetailRow>}
              <DetailRow label="Vendor">{row.vendor_name || '—'}</DetailRow>
              {row.rfq_number && <DetailRow label="RFQ">{row.rfq_number}</DetailRow>}
              {!bid && row.source_bid_id && (
                <DetailRow label="Source bid">
                  <Box
                    component="button"
                    type="button"
                    onClick={() => history.push(url.replace(/\/work-orders\/[^/]+$/, `/work-orders/${row.source_bid_id}`))}
                    sx={{
                      border: 0, background: 'none', padding: 0, cursor: 'pointer',
                      color: IPD_COLORS.primary, fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                    }}
                  >
                    {row.other_reference || 'Open bid'}
                  </Box>
                </DetailRow>
              )}
              {isHumanWoReference(row.quotation_number) && (
                <DetailRow label="Quotation">{row.quotation_number}</DetailRow>
              )}
              {row.assigned_to && <DetailRow label="Assigned to">{row.assigned_to}</DetailRow>}
              {row.planned_start && <DetailRow label="Planned start">{formatDate(row.planned_start)}</DetailRow>}
              {row.expected_completion && <DetailRow label="Expected">{formatDate(row.expected_completion)}</DetailRow>}
              {(!bid || Number(row.grand_total) > 0) && (
                <DetailRow label="Amount">{formatCurrency(row.grand_total)}</DetailRow>
              )}
              {bid && (
                <DetailRow label="Open bid">
                  {row.is_open_bid ? 'Yes — new vendors can register on the public link' : 'No — invited vendors only'}
                </DetailRow>
              )}
              {row.direct_reason && <DetailRow label="Direct reason">{row.direct_reason}</DetailRow>}
              {row.description && <DetailRow label="Scope">{row.description}</DetailRow>}
              {row.breakup && (
                <DetailRow label="Vendor breakup">
                  <WoBreakupList text={row.breakup} />
                </DetailRow>
              )}
              {row.terms_and_conditions && <DetailRow label="Terms & Conditions">{row.terms_and_conditions}</DetailRow>}
              {row.notes && <DetailRow label="Notes">{row.notes}</DetailRow>}
            </SectionCard>

            {bid && (
              <SectionCard title="Vendors" sx={{ mt: 2.5 }}>
                {invitedVendors.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                    No vendors invited on this work bid yet.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {invitedVendors.map((v) => (
                      <Box
                        key={v.id}
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v.label}</Typography>
                        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          {v.quoted ? 'Quoted' : (v.invited ? 'Invited' : 'Registered via link')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </SectionCard>
            )}
          </Grid>

          <Grid item xs={12} md={7}>
            <SectionCard title="Line Items" sx={{ height: '100%' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  sx={{
                    minWidth: 560,
                    '& td, & th': {
                      borderColor: IPD_COLORS.border,
                      color: IPD_COLORS.textPrimary,
                      py: 1.25,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 160 }}>Item / service</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 140 }}>Description</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 88 }}>UOM</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 72 }}>Qty</TableCell>
                      {!bid && <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Rate</TableCell>}
                      {!bid && <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Amount</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={bid ? 4 : 6} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary, py: 3 }}>
                          No lines
                        </TableCell>
                      </TableRow>
                    ) : items.map((line, i) => (
                      <TableRow key={line.id || i}>
                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: 600 }}>
                          {line.item_name || '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', color: IPD_COLORS.textSecondary }}>
                          {hasDisplayValue(line.description) ? line.description : '—'}
                        </TableCell>
                        <TableCell>
                          {(line.uom_code && !isUuidLike(line.uom_code))
                            ? line.uom_code
                            : (getPurchaseUomCode(line.item_id) || line.uom_code || '—')}
                        </TableCell>
                        <TableCell align="right">{asWholeNumber(line.quantity) || line.quantity}</TableCell>
                        {!bid && (
                          <TableCell align="right">
                            {line.rate !== '' ? formatCurrency(asWholeNumber(line.rate) || line.rate) : '—'}
                          </TableCell>
                        )}
                        {!bid && (
                          <TableCell align="right">{formatCurrency(woLineAmounts(line).total)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard title="Activity">
              {(row.events || []).length === 0 && (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>No events yet.</Typography>
              )}
              {(row.events || []).slice().reverse().map((ev) => (
                <Box key={ev.id} sx={{ py: 0.75, borderBottom: `1px solid ${IPD_COLORS.border}` }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{ev.action}</Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {formatWoActivityMeta({
                      ...ev,
                      created_at: formatDateTime(ev.created_at),
                    }).join(' · ')}
                  </Typography>
                </Box>
              ))}
            </SectionCard>
          </Grid>
        </Grid>
      )}

      {activeTab === TAB_VENDORS && bidHistory && (
        <SectionCard title="Invited Vendors">
          <WoPortalLinkCard workOrder={row} onUpdated={load} />
          {invitedVendors.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
              No vendors have been invited or registered on this work bid yet.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Source</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Response</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitedVendors.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{v.label}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          {v.invited ? 'Invited' : 'Registered via link'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={v.quoted ? 'Responded' : 'Awaiting quote'}
                          sx={{
                            height: 20, fontSize: 11, fontWeight: 600,
                            backgroundColor: v.quoted ? IPD_COLORS.success.bg : IPD_COLORS.selectedBg,
                            color: v.quoted ? IPD_COLORS.success.text : IPD_COLORS.textSecondary,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </SectionCard>
      )}

      {activeTab === TAB_QUOTES && showQuotesTab && (
        <SectionCard title="Vendor Quotes">
          {quotes.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2 }}>
              No vendor quotes submitted yet. Invited vendors sign in on the vendor link with OTP.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{
                  minWidth: 640,
                  '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary, py: 1.25 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Submitted</TableCell>
                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Total</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Breakup</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotes.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{q.vendor_name || q.vendor_id || '—'}</TableCell>
                      <TableCell>{q.submitted_at ? formatDate(q.submitted_at) : '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(q.grand_total)}</TableCell>
                      <TableCell><WoBreakupList text={q.breakup} /></TableCell>
                      <TableCell><StatusChip status={q.status || 'SUBMITTED'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </SectionCard>
      )}

      {activeTab === TAB_COMPARISON && showComparisonTab && (
        <SectionCard title="Quote comparison">
          <WoQuoteComparison
            items={items}
            quotes={quotes}
            canAward={status === 'PUBLISHED' && canAward}
            onAward={(q) => setDialog({
              action: 'award',
              quoteId: q.id,
              title: 'Award this work order',
              desc: `Issue ${row.wo_number} to ${q.vendor_name || 'this vendor'} with their quoted rates? The same work-order number stays. You can then start and close the job on this record.`,
            })}
          />
        </SectionCard>
      )}

      <ConfirmDialog
        open={Boolean(dialog)}
        loading={acting}
        title={dialog?.title}
        description={dialog?.desc}
        destructive={dialog?.destructive}
        optionalReason={dialog?.action === 'complete' || dialog?.action === 'hold'}
        reasonLabel={dialog?.action === 'complete' ? 'Completed by / remarks' : 'Reason'}
        reasonValue={reason}
        onReasonChange={setReason}
        onConfirm={runAction}
        onCancel={() => { setDialog(null); setReason(''); }}
      />
    </Box>
  );
}
